// FILE: AgentAvatar.tsx
// Purpose: Draw a managed agent's photo, or initials over its stable accent colour.
// Exports: AgentAvatar

import { memo } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { resolveServerHttpOrigin } from "~/lib/serverOrigin";
import { cn } from "~/lib/utils";
import { agentAvatarFallbackStyle, agentAvatarSrc, agentInitials } from "./AgentAvatar.logic";

export type AgentAvatarSize = "xs" | "sm" | "md" | "lg";

const SIZE_CLASSES: Record<AgentAvatarSize, string> = {
  xs: "size-5 text-[9px]",
  sm: "size-6 text-[10px]",
  md: "size-8 text-[12px]",
  lg: "size-16 text-[22px]",
};

interface AgentAvatarProps {
  readonly agentId: string;
  readonly displayName?: string | null | undefined;
  readonly avatarFileName?: string | null | undefined;
  /** Nickname used to derive the same colour as subagentPresentation. */
  readonly nickname?: string | null | undefined;
  /** Already-resolved subagent colour, when the caller has a presentation. */
  readonly accentColor?: string | null | undefined;
  readonly size?: AgentAvatarSize;
  readonly className?: string;
  readonly decorative?: boolean;
}

export const AgentAvatar = memo(function AgentAvatar({
  agentId,
  displayName,
  avatarFileName,
  nickname,
  accentColor,
  size = "sm",
  className,
  decorative = true,
}: AgentAvatarProps) {
  const src = agentAvatarSrc({
    agentId,
    avatarFileName,
    origin: resolveServerHttpOrigin(),
  });
  const label = displayName?.trim() || "";
  const initials = agentInitials(label);
  const fallbackSeed = nickname?.trim() || label || agentId;

  return (
    // Remount when the image identity changes so a removed/replaced photo can
    // correctly return to the fallback state in Base UI's avatar state machine.
    <Avatar key={src ?? "initials"} className={cn(SIZE_CLASSES[size], className)}>
      {src === null ? null : (
        <AvatarImage
          src={src}
          alt={decorative ? "" : label || "Agent"}
          {...(decorative ? { "aria-hidden": true } : {})}
        />
      )}
      <AvatarFallback style={agentAvatarFallbackStyle(fallbackSeed, accentColor)}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
});
